import * as Styles from './styles';
import { FilterTag } from '../constants/types';
import { useViewport } from 'utils/responsive';
import type { GroupedLedgerRow } from '../utils/transformGroupedLedger';
import type { LedgerRow } from './helpers';
import {
    aggregateRowsByDate,
    formatAmount,
    formatGroupedBalance,
    getAmountForColumn,
    getAmountVariant,
    getColumnVisibility,
    getWalletRefundAmount,
} from './helpers';

type LedgerTableProps = {
    rows: LedgerRow[] | GroupedLedgerRow[];
    activeTag: FilterTag;
    isGrouped?: boolean;
    isLoading?: boolean;
};

function LedgerTable({
    rows,
    activeTag,
    isGrouped = false,
    isLoading = false,
}: LedgerTableProps) {
    const { isMobile } = useViewport();
    const { showDeposits, showPremium, showRefund } =
        getColumnVisibility(activeTag);

    if (isLoading) {
        return (
            <Styles.TableSkeletonWrapper>
                <Styles.SkeletonBlock style={{ width: '38%' }} />
                <Styles.SkeletonBlock style={{ width: '100%' }} />
                <Styles.SkeletonBlock style={{ width: '92%' }} />
                <Styles.SkeletonBlock style={{ width: '96%' }} />
                {isMobile && <Styles.SkeletonBlock style={{ width: '88%' }} />}
            </Styles.TableSkeletonWrapper>
        );
    }

    const groupedRows = isGrouped ? (rows as GroupedLedgerRow[]) : [];
    const transactionRows = !isGrouped ? (rows as LedgerRow[]) : [];

    const filteredGroupedRows = groupedRows;

    const tableRows: LedgerRow[] = isGrouped
        ? []
        : activeTag === FilterTag.ALL
        ? transactionRows
        : transactionRows.filter((row) => row.tag === activeTag);

    if (isMobile) {
        if (isGrouped) {
            return (
                <Styles.MobileWrapper>
                    <Styles.MobileTitle>Transactions</Styles.MobileTitle>
                    {filteredGroupedRows.map((day) => (
                        <Styles.MobileItem key={day.date}>
                            <Styles.MobileTopRow>
                                <Styles.MobileDate>
                                    {day.date}
                                </Styles.MobileDate>
                                <Styles.MobileBalance>
                                    {`Bal: ${formatGroupedBalance(
                                        day.balance
                                    )}`}
                                </Styles.MobileBalance>
                            </Styles.MobileTopRow>
                            <Styles.MobileCategoriesRow>
                                {showDeposits && (
                                    <Styles.MobileCategory>
                                        <Styles.MobileCategoryLabel>
                                            Deposits
                                        </Styles.MobileCategoryLabel>
                                        <Styles.TableBodyCellAmount
                                            as="div"
                                            variant={
                                                day.deposits > 0
                                                    ? 'positive'
                                                    : 'zero'
                                            }
                                        >
                                            {formatAmount(day.deposits)}
                                        </Styles.TableBodyCellAmount>
                                    </Styles.MobileCategory>
                                )}
                                {showPremium && (
                                    <Styles.MobileCategory>
                                        <Styles.MobileCategoryLabel>
                                            Premium
                                        </Styles.MobileCategoryLabel>
                                        <Styles.TableBodyCellAmount
                                            as="div"
                                            variant={
                                                day.premium > 0
                                                    ? 'negative'
                                                    : 'zero'
                                            }
                                        >
                                            {formatAmount(day.premium)}
                                        </Styles.TableBodyCellAmount>
                                    </Styles.MobileCategory>
                                )}
                                {showRefund && (
                                    <Styles.MobileCategory>
                                        <Styles.MobileCategoryLabel>
                                            Refund
                                        </Styles.MobileCategoryLabel>
                                        <Styles.TableBodyCellAmount
                                            as="div"
                                            variant={
                                                day.refund > 0
                                                    ? 'positive'
                                                    : 'zero'
                                            }
                                        >
                                            {formatAmount(day.refund)}
                                        </Styles.TableBodyCellAmount>
                                    </Styles.MobileCategory>
                                )}
                            </Styles.MobileCategoriesRow>
                        </Styles.MobileItem>
                    ))}
                </Styles.MobileWrapper>
            );
        }

        const aggregated = aggregateRowsByDate(tableRows);
        return (
            <Styles.MobileWrapper>
                <Styles.MobileTitle>Transactions</Styles.MobileTitle>
                {aggregated.map((day) => (
                    <Styles.MobileItem key={day.date}>
                        <Styles.MobileTopRow>
                            <Styles.MobileDate>{day.date}</Styles.MobileDate>
                            <Styles.MobileBalance>
                                {`Bal: ${day.balance}`}
                            </Styles.MobileBalance>
                        </Styles.MobileTopRow>
                        <Styles.MobileCategoriesRow>
                            {showDeposits && (
                                <Styles.MobileCategory>
                                    <Styles.MobileCategoryLabel>
                                        Deposits
                                    </Styles.MobileCategoryLabel>
                                    <Styles.TableBodyCellAmount
                                        as="div"
                                        variant={
                                            day.deposits > 0
                                                ? 'positive'
                                                : 'zero'
                                        }
                                    >
                                        {formatAmount(day.deposits)}
                                    </Styles.TableBodyCellAmount>
                                </Styles.MobileCategory>
                            )}
                            {showPremium && (
                                <Styles.MobileCategory>
                                    <Styles.MobileCategoryLabel>
                                        Premium
                                    </Styles.MobileCategoryLabel>
                                    <Styles.TableBodyCellAmount
                                        as="div"
                                        variant={
                                            day.premium > 0
                                                ? 'negative'
                                                : 'zero'
                                        }
                                    >
                                        {formatAmount(day.premium)}
                                    </Styles.TableBodyCellAmount>
                                </Styles.MobileCategory>
                            )}
                            {showRefund && (
                                <Styles.MobileCategory>
                                    <Styles.MobileCategoryLabel>
                                        Refund
                                    </Styles.MobileCategoryLabel>
                                    <Styles.TableBodyCellAmount
                                        as="div"
                                        variant={
                                            day.refund > 0 ? 'positive' : 'zero'
                                        }
                                    >
                                        {formatAmount(day.refund)}
                                    </Styles.TableBodyCellAmount>
                                </Styles.MobileCategory>
                            )}
                        </Styles.MobileCategoriesRow>
                    </Styles.MobileItem>
                ))}
            </Styles.MobileWrapper>
        );
    }

    if (isGrouped) {
        return (
            <Styles.TableWrapper>
                <Styles.Table>
                    <thead>
                        <Styles.TableHeaderRow>
                            <Styles.TableHeaderCell>
                                Date
                            </Styles.TableHeaderCell>
                            {showDeposits && (
                                <Styles.TableHeaderCell>
                                    Deposits
                                </Styles.TableHeaderCell>
                            )}
                            {showPremium && (
                                <Styles.TableHeaderCell>
                                    Premium
                                </Styles.TableHeaderCell>
                            )}
                            {showRefund && (
                                <Styles.TableHeaderCell>
                                    Wallet Refund
                                </Styles.TableHeaderCell>
                            )}
                            <Styles.TableHeaderCell>
                                Total Balance
                            </Styles.TableHeaderCell>
                        </Styles.TableHeaderRow>
                    </thead>
                    <tbody>
                        {filteredGroupedRows.map((row) => (
                            <Styles.TableBodyRow key={row.date}>
                                <Styles.TableBodyCell>
                                    {row.date}
                                </Styles.TableBodyCell>
                                {showDeposits && (
                                    <Styles.TableBodyCellAmount
                                        variant={
                                            row.deposits > 0
                                                ? 'positive'
                                                : 'zero'
                                        }
                                    >
                                        {row.deposits > 0
                                            ? `+${formatGroupedBalance(
                                                  row.deposits
                                              )}`
                                            : '0.0'}
                                    </Styles.TableBodyCellAmount>
                                )}
                                {showPremium && (
                                    <Styles.TableBodyCellAmount
                                        variant={
                                            row.premium > 0
                                                ? 'negative'
                                                : 'zero'
                                        }
                                    >
                                        {row.premium > 0
                                            ? `-${formatGroupedBalance(
                                                  row.premium
                                              )}`
                                            : '0.0'}
                                    </Styles.TableBodyCellAmount>
                                )}
                                {showRefund && (
                                    <Styles.TableBodyCellAmount
                                        variant={
                                            row.refund > 0 ? 'positive' : 'zero'
                                        }
                                    >
                                        {row.refund > 0
                                            ? `+${formatGroupedBalance(
                                                  row.refund
                                              )}`
                                            : '0.0'}
                                    </Styles.TableBodyCellAmount>
                                )}
                                <Styles.TableBodyCellBalance>
                                    {formatGroupedBalance(row.balance)}
                                </Styles.TableBodyCellBalance>
                            </Styles.TableBodyRow>
                        ))}
                    </tbody>
                </Styles.Table>
            </Styles.TableWrapper>
        );
    }

    return (
        <Styles.TableWrapper>
            <Styles.Table>
                <thead>
                    <Styles.TableHeaderRow>
                        <Styles.TableHeaderCell>Date</Styles.TableHeaderCell>
                        {showDeposits && (
                            <Styles.TableHeaderCell>
                                Deposits
                            </Styles.TableHeaderCell>
                        )}
                        {showPremium && (
                            <Styles.TableHeaderCell>
                                Premium
                            </Styles.TableHeaderCell>
                        )}
                        {showRefund && (
                            <Styles.TableHeaderCell>
                                Wallet Refund
                            </Styles.TableHeaderCell>
                        )}
                        <Styles.TableHeaderCell>
                            Total Balance
                        </Styles.TableHeaderCell>
                    </Styles.TableHeaderRow>
                </thead>
                <tbody>
                    {tableRows.map((row, index) => (
                        <Styles.TableBodyRow key={`${row.date}-${index}`}>
                            <Styles.TableBodyCell>
                                {row.date}
                            </Styles.TableBodyCell>
                            {showDeposits && (
                                <Styles.TableBodyCellAmount
                                    variant={getAmountVariant(
                                        getAmountForColumn(
                                            row,
                                            FilterTag.DEPOSITS
                                        )
                                    )}
                                >
                                    {getAmountForColumn(
                                        row,
                                        FilterTag.DEPOSITS
                                    )}
                                </Styles.TableBodyCellAmount>
                            )}
                            {showPremium && (
                                <Styles.TableBodyCellAmount
                                    variant={getAmountVariant(
                                        getAmountForColumn(
                                            row,
                                            FilterTag.PREMIUM
                                        )
                                    )}
                                >
                                    {getAmountForColumn(row, FilterTag.PREMIUM)}
                                </Styles.TableBodyCellAmount>
                            )}
                            {showRefund && (
                                <Styles.TableBodyCellAmount
                                    variant={getAmountVariant(
                                        getWalletRefundAmount(row)
                                    )}
                                >
                                    {getWalletRefundAmount(row)}
                                </Styles.TableBodyCellAmount>
                            )}
                            <Styles.TableBodyCellBalance>
                                {row.balance}
                            </Styles.TableBodyCellBalance>
                        </Styles.TableBodyRow>
                    ))}
                </tbody>
            </Styles.Table>
        </Styles.TableWrapper>
    );
}

export default LedgerTable;
